import { useState } from 'react';

import { DeleteOutlined, EyeOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons';
import { Form, Image, message, Popconfirm, Tooltip, Upload } from 'antd';
import { chunkUploadMedia } from 'utils';

import { getAcceptedFileType, getMaxFileSize, isExtensionAllowed, isPreviewableMedia, resolvePosterUrl } from './helpers';
import MediaThumb from './MediaThumb';
import './style.less';

/**
 * Medya yükleme alanı. Hiç dosya yokken zengin dashed dragger render eder; en az bir
 * dosya yüklendiğinde tek satıra sıkışan ayrı liste yerine büyük kare thumbnail'lardan
 * oluşan responsive bir grid + sonda dashed "Ekle" kartı gösterir. Dosya isimleri
 * gösterilmez; her kartta hover'da (mobilde her zaman) silme butonu çıkar. Önizlenebilir
 * görsel kartlarına tıklanınca dosya tam-boy lightbox'ta açılır (gruptakiler arasında `‹ ›`).
 *
 * Yükleme mantığı (chunk upload, uzantı doğrulama, durum mesajları) hem dragger hem
 * "Ekle" kartı için tek `uploadProps` kaynağından paylaşılır.
 *
 * @param {Object} props
 * @param {boolean} [props.readOnly=false] - true ise salt-görüntüleme: dragger, "Ekle" kartı ve
 *   her karttaki silme butonu gizlenir; yalnız mevcut dosyaların thumbnail'ları gösterilir. Dosya yoksa
 *   sade bir placeholder çıkar. Read-only detay modunda (MediaContentForm → SupervisorEditMediaContent) geçilir.
 */
function UploadArea({ readOnly = false }) {
  const form = Form.useFormInstance();

  const media = Form.useWatch('media', form);
  const mediaType = Form.useWatch('mediaType', form);

  // Büyük görüntüleme (lightbox) durumu; `current`, previewItems içindeki index'tir.
  const [preview, setPreview] = useState({ visible: false, current: 0 });

  // Seçili medya türüne karşılık gelen izinli uzantı listesi (accept prop ile aynı kaynak)
  const acceptedFileType = getAcceptedFileType(mediaType);

  const handleChange = (info) => {
    if (info.file.status === 'done') {
      message.destroy();
      message.success('Dosya yüklendi');
    } else if (info.file.status === 'error') {
      message.destroy();
      message.error('Dosya yüklenirken bir hata oluştu');
    } else if (info.file.status === 'uploading') {
      message.loading('Dosya yükleniyor', 0);
    }
  };

  // Tüm ön-doğrulama (uzantı, boş dosya, boyut) burada yapılır; "All Files" ile accept
  // bypass edilebildiğinden uzantı politikası da tek gerçek kapı olarak burada uygulanır.
  const beforeUpload = (file) => {
    // 1) Uzantı: bariz türlerde whitelist eşleşmesi, belirsiz türlerde deny-list dışı olma
    if (!isExtensionAllowed(file.name, mediaType)) {
      message.error(
        acceptedFileType
          ? `Seçili dosya türü için izin verilen uzantılar: ${acceptedFileType}`
          : 'Güvenlik nedeniyle bu dosya türü yüklenemez',
      );
      return Upload.LIST_IGNORE;
    }
    // 2) Boş dosya
    if (file.size === 0) {
      message.error('Dosya boş olamaz');
      return Upload.LIST_IGNORE;
    }
    // 3) Boyut: bariz türler 25MB, belirsiz/kaynak türler 100MB (türe duyarlı)
    const maxSize = getMaxFileSize(mediaType);
    if (file.size > maxSize) {
      message.error(`Dosya boyutu ${Math.round(maxSize / (1024 * 1024))}MB sınırını aşamaz`);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      const [res] = await chunkUploadMedia('articleContent', [file]);

      const updatedFile = {
        uid: file.uid,
        id: res.media.id,
        mediaID: `/api/media/${res.media.id}`,
        name: file.name,
        url: res.media.fileFullUrl,
        // poster, response'ta `media`'nın KARDEŞİ olarak gelir; thumbnail için saklanır
        poster: res.poster,
        response: res.media,
      };
      form.setFieldsValue({
        media: [...(media || []), updatedFile],
      });
      onSuccess('done');
    } catch (error) {
      onError('error');
    }
  };

  const handleDelete = (file) => {
    form.setFieldsValue({
      media: (media || []).filter((item) => item.id !== file.id),
    });
  };

  // Dragger ve "Ekle" kartı arasında paylaşılan yükleme yapılandırması (tek kaynak)
  const uploadProps = {
    disabled: !mediaType,
    showUploadList: false,
    accept: acceptedFileType,
    onChange: handleChange,
    beforeUpload,
    customRequest,
  };

  const hasMedia = Array.isArray(media) && media.length > 0;

  // Upload anı kartında dosya `response`'ta, kayıtlı (edit) kartta kökte durur.
  const getFileMedia = (file) => file.response || file;

  // Lightbox grubunun sırası; hem index çözümünde hem gizli <Image> kaydında aynı liste kullanılır.
  const previewItems = hasMedia ? media.filter((file) => isPreviewableMedia(getFileMedia(file), mediaType)) : [];

  const openPreview = (file) => {
    const index = previewItems.indexOf(file);
    if (index >= 0) setPreview({ visible: true, current: index });
  };

  return (
    <>
      <Tooltip title={!readOnly && !mediaType ? 'Lütfen Önce dosya türü seçiniz' : ''}>
        <Form.Item label="Dosya Yükleme">
          {!hasMedia ? (
            // Read-only'de yükleme kapalı → dragger yerine sade bir placeholder gösterilir.
            readOnly ? (
              <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>Dosya bulunmuyor</div>
            ) : (
              <Upload.Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Dosyayı bu alana sürükleyin ya da tıklayıp seçin</p>
                <p className="ant-upload-hint">Tek seferde bir veya birden fazla dosya yükleyebilirsiniz.</p>
              </Upload.Dragger>
            )
          ) : (
            <div className="media-upload-grid">
              {media.map((file, index) => {
                const previewable = previewItems.includes(file);
                const thumb = (
                  <MediaThumb
                    src={resolvePosterUrl(file, mediaType)}
                    mediaType={mediaType}
                    alt={file.name}
                    fill
                  />
                );

                return (
                  <div
                    className="media-upload-grid__card"
                    key={file.id ?? file.uid ?? index}
                  >
                    {/* Tıklama alanı kartın kendisi değil thumbnail sarmalayıcısıdır: Popconfirm
                      portal'ının (Evet/İptal) React event'leri kart üzerinden yayılıp lightbox açmasın. */}
                    {previewable ? (
                      <div
                        className="media-upload-grid__preview"
                        role="button"
                        tabIndex={0}
                        aria-label={`${file.name} — büyük görüntüle`}
                        onClick={() => openPreview(file)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault();
                          openPreview(file);
                        }}
                      >
                        {thumb}
                        <span className="media-upload-grid__preview-mask">
                          <EyeOutlined />
                        </span>
                      </div>
                    ) : (
                      thumb
                    )}
                    {/* Silme yalnız düzenlenebilir modda; read-only'de thumbnail salt-görüntülenir. */}
                    {!readOnly && (
                      <Popconfirm
                        placement="left"
                        title="Silmek İstediğinizden Emin misiniz?"
                        okText="Evet"
                        cancelText="İptal"
                        onConfirm={() => handleDelete(file)}
                      >
                        <button
                          type="button"
                          className="media-upload-grid__delete"
                          aria-label="Dosyayı sil"
                        >
                          <DeleteOutlined />
                        </button>
                      </Popconfirm>
                    )}
                  </div>
                );
              })}
              {/* Yeni dosya ekleme: tıklayınca dosya seçici açılır (grid'de sürükle-bırak yok).
                  Read-only'de gizlenir. */}
              {!readOnly && (
                <Upload
                  {...uploadProps}
                  className="media-upload-grid__add-wrapper"
                >
                  <div className="media-upload-grid__add">
                    <PlusOutlined />
                    <span>Ekle</span>
                  </div>
                </Upload>
              )}
            </div>
          )}
        </Form.Item>
      </Tooltip>
      {/**
       * Kontrollü önizleme grubu; thumbnail'lar grid'de çizildiği için gizlidir. Lightbox body'e
       * portallandığından `display:none` yalnız kayıt amaçlı <img>'leri gizler. `src` tam-boy dosyadır.
       */}
      <div style={{ display: 'none' }}>
        <Image.PreviewGroup
          preview={{
            visible: preview.visible,
            current: preview.current,
            onVisibleChange: (visible) => setPreview((prev) => ({ ...prev, visible })),
          }}
        >
          {previewItems.map((file, index) => (
            <Image
              key={file.id ?? file.uid ?? index}
              src={getFileMedia(file).fileFullUrl}
              alt={file.name}
            />
          ))}
        </Image.PreviewGroup>
      </div>
      {/**
       * Medya değerinin tek kaynağı. Görünür "Dosya Yükleme" Form.Item'ına name verilemez:
       * antd child'a kendi onChange'ini enjekte edip Upload'ın handleChange'ini (yükleme
       * mesajları) ezerdi. Bu yüzden değer ayrı, gizli bir alanda tutulur.
       *
       * Dosya yükleme ZORUNLU DEĞİL (bilinçli): kullanıcı dosya yüklemeden de (yalnız
       * kaynak/link/açıklama ile) medya teklifi gönderebilir — bu yüzden `rules` yok.
       */}
      <Form.Item
        name="media"
        initialValue={[]}
        style={{ marginBottom: 0 }}
      >
        <span style={{ display: 'none' }} />
      </Form.Item>
    </>
  );
}
export default UploadArea;
